import React, { useEffect, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import api from "../../api";
import { Modal } from "react-bootstrap";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";

const LIMIT = 10;

export const Students = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [show, setShow] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const openEdit = (item) => setEditItem(item);
  const closeEdit = () => setEditItem(null);

  const getData = async (searchVal = search, pageVal = page) => {
    try {
      setLoader(true);
      const response = await api.getStudents({
        search: searchVal,
        page: pageVal,
        limit: LIMIT,
      });
      if (response?.success) {
        setData(response?.students || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 1);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData(search, 1);
  }, []);

  const handleSearch = () => {
    setPage(1);
    getData(search, 1);
  };

  const handleClear = () => {
    setSearch("");
    setPage(1);
    getData("", 1);
  };

  const handlePageChange = (e, value) => {
    setPage(value);
    getData(search, value);
  };

  const deleteStudents = async (id) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      const response = await api.deleteStudents(id);
      if (response?.success) {
        notify.success("Student deleted successfully!");
        getData();
      }
    } catch (error) {
      notify.error("Something went wrong!");
    }
  };

  return (
    <>
      <div className="DashboardMain">
        <div className="row">
          <div className="col">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="mb-0">Students ({total})</h3>
              <div className="input-group" style={{ width: "350px" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or roll no..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleClear}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                )}
                <button className="btn btn-success" onClick={handleSearch}>
                  <i className="fa-solid fa-search"></i>
                </button>
              </div>
            </div>

            {loader ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" />
              </div>
            ) : (
              <Table data={data} deleteStudents={deleteStudents} openEdit={openEdit} />
            )}

            {data && data.length > 0 && total > LIMIT && (
              <div className="pagination-container">
                <Stack spacing={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#10b981",
                        color: "white",
                        "&:hover": { backgroundColor: "#059669" },
                      },
                      "& .MuiPaginationItem-root": {
                        "&:hover": {
                          backgroundColor: "#d1fae5",
                          color: "#059669",
                        },
                      },
                    }}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>

        <div className="AddEmpBtn" onClick={handleShow}>
          <i className="fa-solid fa-plus"></i>
        </div>
      </div>

      <CreateStudentsModal show={show} handleClose={handleClose} getVehicle={getData} />
      {editItem && <EditStudentModal item={editItem} handleClose={closeEdit} getStudents={getData} />}
    </>
  );
};

const NumberPlate = ({ number }) => (
  <div style={{ display: "inline-block", background: "#fff", border: "2.5px solid #222", borderRadius: 6, overflow: "hidden", minWidth: 110, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}>
    <div style={{ background: "repeating-linear-gradient(90deg, #c0392b 0px, #c0392b 10px, #e74c3c 10px, #e74c3c 14px, #c0392b 14px, #c0392b 24px)", height: 8 }} />
    <div style={{ padding: "4px 10px 2px", textAlign: "center", fontWeight: 900, fontSize: 14, letterSpacing: 3, color: "#111", fontFamily: "'Courier New', monospace", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {number || "—"}
    </div>
    <div style={{ textAlign: "center", fontSize: 8, fontWeight: 700, letterSpacing: 4, color: "#444", paddingBottom: 4, textTransform: "uppercase" }}>SINDH</div>
  </div>
);

const Table = ({ data, deleteStudents, openEdit }) => {
  const formatDate = (date) => moment(date).format("MMM Do YYYY, h:mm a");

  return (
    <div className="TableMain mt-3">
      <div className="table-responsive">
        <table className="table table-hover drivers-table">
          <thead>
            <tr>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Name</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Roll No.</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Phone</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Batch</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Department</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Semester</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Fee Plan</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Fee Amount</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Assign Route</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Assign Vehicle</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Status</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Added At</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, key) => (
                <tr key={key}>
                  <td>
                    <div className="table-text-dark w-max-c db" style={{ textTransform: "capitalize" }}>{item?.name}</div>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ fontWeight: 600 }}>{item?.rollNo || "—"}</span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db">{item?.phone || "—"}</span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db">{item?.batch || "—"}</span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db">{item?.department || "—"}</span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db">{item?.semester || "—"}</span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ textTransform: "capitalize" }}>{item?.feePlan || "—"}</span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ fontWeight: 600, color: "#065f46" }}>
                      {item?.feeAmount ? `Rs. ${item.feeAmount}` : "—"}
                    </span>
                  </td>
                  <td>
                    <div style={{ width: "max-content", textTransform: "capitalize", display: "flex", alignItems: "center", gap: 6 }} className="table-text-dark">
                      <i className="fa-solid fa-location-dot" style={{ color: "#10b981", fontSize: 13 }} />
                      {item?.routeId?.name || "—"}
                    </div>
                  </td>
                  <td>
                    {item?.vehicleId?.numberPlate
                      ? <NumberPlate number={item.vehicleId.numberPlate} />
                      : "—"}
                  </td>
                  <td>
                    <span style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: "capitalize",
                      background: item?.status === "active" ? "#d1fae5" : "#fee2e2",
                      color: item?.status === "active" ? "#065f46" : "#991b1b",
                    }}>
                      {item?.status || "—"}
                    </span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ fontSize: 13 }}>{formatDate(item?.createdAt)}</span>
                  </td>
                  <td>
                    <div className="d-flex gap-2 align-items-center">
                      <div className="edit p-2 cursor-pointer" onClick={() => openEdit(item)}>
                        <i className="fa-solid fa-pen-to-square"></i>
                      </div>
                      <div className="delete p-2 cursor-pointer" onClick={() => deleteStudents(item?._id)}>
                        <i className="fa-solid fa-trash"></i>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={13} className="text-center py-4 text-muted">No students found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CreateStudentsModal = ({ show, handleClose, getVehicle }) => {
  const [vehicles, setVehicle] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [data, setData] = useState({});
  const [loader, setLoader] = useState(false);

  const handleSubmit = async () => {
    try {
      let payload = {
        name: data?.name,
        rollNo: data?.rollNo,
        phone: data?.phone,
        batch: data?.batch,
        department: data?.department,
        semester: data?.semester,
        routeId: data?.routeId,
        vehicleId: data?.vehicleId,
        feePlan: data?.feePlan,
        feeAmount: data?.feeAmount,
      };

      const response = await api.addStudents(payload);
      if (response?.success) {
        getVehicle();
        notify.success("Student successfully added!");
        setData({});
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  const onTextChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const getVehicleData = async () => {
    try {
      setLoader(true);
      const response = await api.getVehicle();
      if (response?.success) {
        setVehicle(response?.vehicles || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const getRouteData = async () => {
    try {
      setLoader(true);
      const response = await api.getRoute();
      if (response?.success) {
        setRoutes(response?.routes || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getRouteData();
    getVehicleData();
  }, []);

  return (
    <Modal show={show} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}>
          <i className="fa-solid fa-xmark" />
        </div>
        <div className="h3">Add Students</div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                name="name"
                className="form-control"
                onChange={onTextChange}
              />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Roll Number</label>
              <input
                type="text"
                name="rollNo"
                className="form-control"
                onChange={onTextChange}
              />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="number"
                name="phone"
                className="form-control"
                onChange={onTextChange}
              />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Batch</label>
              <input type="text" name="batch" className="form-control" onChange={onTextChange} placeholder="e.g. 2022" />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Department</label>
              <input type="text" name="department" className="form-control" onChange={onTextChange} placeholder="e.g. MBBS" />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Semester</label>
              <input type="text" name="semester" className="form-control" onChange={onTextChange} placeholder="e.g. 3rd" />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Fee Amount</label>
              <input
                type="number"
                name="feeAmount"
                className="form-control"
                onChange={onTextChange}
              />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Fee Plan</label>
              <select
                name="feePlan"
                className="form-control"
                onChange={onTextChange}
              >
                <option value="">Select</option>
                {["monthly", "half-yearly", "yearly"].map((item, key) => (
                  <option value={item} key={key}>
                    {item.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Assigned Route</label>
              <select
                name="routeId"
                className="form-control"
                onChange={onTextChange}
              >
                <option value="">Select</option>
                {routes.map((item, key) => (
                  <option value={item?._id} key={key}>
                    {item?.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Assigned Vehicle</label>
              <select
                name="vehicleId"
                className="form-control"
                onChange={onTextChange}
              >
                <option value="">Select</option>
                {vehicles.map((item, key) => (
                  <option value={item?._id} key={key}>
                    {item?.numberPlate}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      </div>
    </Modal>
  );
};

const EditStudentModal = ({ item, handleClose, getStudents }) => {
  const [vehicles, setVehicle] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [data, setData] = useState({
    name: item?.name || "",
    rollNo: item?.rollNo || "",
    phone: item?.phone || "",
    batch: item?.batch || "",
    department: item?.department || "",
    semester: item?.semester || "",
    feeAmount: item?.feeAmount || "",
    feePlan: item?.feePlan || "",
    routeId: item?.routeId?._id || "",
    vehicleId: item?.vehicleId?._id || "",
    status: item?.status || "active",
  });
  const [loader, setLoader] = useState(false);
  const onTextChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      const response = await api.updateStudents(item._id, data);
      if (response?.success) {
        getStudents();
        notify.success("Student updated successfully!");
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoader(true);
        const [v, r] = await Promise.all([api.getVehicle(), api.getRoute()]);
        if (v?.success) setVehicle(v?.vehicles || []);
        if (r?.success) setRoutes(r?.routes || []);
      } catch (e) {} finally { setLoader(false); }
    })();
  }, []);

  return (
    <Modal show={true} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">Edit Student</div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Name</label>
              <input type="text" name="name" className="form-control" value={data?.name} onChange={onTextChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Roll Number</label>
              <input type="text" name="rollNo" className="form-control" value={data?.rollNo} onChange={onTextChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Phone</label>
              <input type="number" name="phone" className="form-control" value={data?.phone} onChange={onTextChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Batch</label>
              <input type="text" name="batch" className="form-control" value={data?.batch} onChange={onTextChange} placeholder="e.g. 2022" />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Department</label>
              <input type="text" name="department" className="form-control" value={data?.department} onChange={onTextChange} placeholder="e.g. MBBS" />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Semester</label>
              <input type="text" name="semester" className="form-control" value={data?.semester} onChange={onTextChange} placeholder="e.g. 3rd" />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Fee Amount</label>
              <input type="number" name="feeAmount" className="form-control" value={data?.feeAmount} onChange={onTextChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Fee Plan</label>
              <select name="feePlan" className="form-control" value={data?.feePlan} onChange={onTextChange}>
                <option value="">Select</option>
                {["monthly","half-yearly","yearly"].map((item, key) => <option value={item} key={key}>{item.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Assigned Route</label>
              <select name="routeId" className="form-control" value={data?.routeId} onChange={onTextChange}>
                <option value="">Select</option>
                {routes.map((item, key) => <option value={item?._id} key={key}>{item?.name}</option>)}
              </select>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Assigned Vehicle</label>
              <select name="vehicleId" className="form-control" value={data?.vehicleId} onChange={onTextChange}>
                <option value="">Select</option>
                {vehicles.map((item, key) => <option value={item?._id} key={key}>{item?.numberPlate}</option>)}
              </select>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Status</label>
              <select name="status" className="form-control" value={data?.status} onChange={onTextChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Update</button>
        </div>
      </div>
    </Modal>
  );
};
